import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { SCRAPE_CATEGORY_CACHE_KEY, SCRAPE_CATEGORY_KEY } from "./keys";

import { ScrapedCategoryRepository } from "@/repositories/scrape-category";
import { PaginatedResponse } from "@/types";
import { ScrapedCategory } from "@/types/scrape-category";

export const useRefreshScrapedCategory = () => {
  const queryClient = useQueryClient();
  const scrapedCategoryRepository = useRef(new ScrapedCategoryRepository());

  return useMutation({
    mutationFn: async (id: string) => {
      return await scrapedCategoryRepository.current.findById(id);
    },
    onSuccess: (freshCategory: ScrapedCategory) => {
      // Update the DETAIL cache
      queryClient.setQueryData(
        SCRAPE_CATEGORY_CACHE_KEY.DETAIL(freshCategory.id),
        freshCategory,
      );

      // Update all LIST caches that might contain this category
      queryClient.setQueriesData(
        {
          predicate: (query) =>
            query.queryKey[0] === SCRAPE_CATEGORY_KEY &&
            query.queryKey[1] === "LIST",
        },
        (oldData) => {
          if (!oldData) return oldData;
          const cachedData = oldData as PaginatedResponse<ScrapedCategory>;

          // Check if this category exists in the current list
          const categoryExists = cachedData.data.some(
            (cat) => cat.id === freshCategory.id,
          );

          if (!categoryExists) return oldData;

          // Update the category in the list
          return {
            ...cachedData,
            data: cachedData.data.map((cat) =>
              cat.id === freshCategory.id ? freshCategory : cat,
            ),
          };
        },
      );
    },
  });
};
